import { Text, Box, Flex } from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { WipConfig } from '../../types';
import { AddIconButton } from 'packages/common-frontend/components/IconButton';
import { AddButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddButton';
import { AddPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddPropertyModal';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { createBankAccountEntityShell } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditBankAccountModal';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditPartyEntityConfigModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPartyConfigModal';
import { EditPartyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPartyModal';
import { createUserEntityShell } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditUserModal';
import { BankAccountEntityAccordionTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/BankAccountEntity';
import { BaseOrganizationEntity } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/BaseOrganizationEntity';
import { PartyEntityConfig } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/PartyEntityConfig';
import { UserEntityAccordionTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/UserEntity';
import { PropertyTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyTable';

type PartyEntityProps = {
  party: CreditAgreement.DomainEntity.Items.PartyEntity;
  partyKey: CreditAgreement.DomainEntity.Items.PartyType;
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
};
export const PartyEntity = ({
  party,
  basePropertyKey,
  wipConfig,
  partyKey,
  idx,
}: PartyEntityProps) => {
  const [isEditPartyOpen, setIsEditPartyOpen] = useState(false);
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false);
  const [isEditConfigOpen, setIsEditConfigOpen] = useState(false);

  const basePropertyKeyWithIdx = `${basePropertyKey}[${idx}]`;

  const sanitizedProperties = _.omitBy(
    _.omit(
      party,
      'users',
      'entity',
      'type',
      'customerType',
      'bankAccounts',
      '_isWrittenIn_',
      'section',
      '_config_',
    ),
    _.isNil,
  );

  const propertyPairs = _.toPairs(sanitizedProperties);

  return (
    <Box mt="20px" mb="20px" borderWidth="1px">
      <Flex alignItems="center" margin="20px">
        <BaseOrganizationEntity party={party}>
          {wipConfig && 'Action'}
          {wipConfig?.isMutateAllowed(basePropertyKeyWithIdx) && (
            <>
              <EditButton setIsOpen={setIsEditPartyOpen} />
              <EditPartyModal
                setIsOpen={setIsEditPartyOpen}
                isOpen={isEditPartyOpen}
                basePropertyKey={basePropertyKeyWithIdx}
                wipConfig={wipConfig}
                partyKey={partyKey}
              />
              {!getIn(wipConfig.wipShell, basePropertyKeyWithIdx) &&
                !wipConfig.arePartiesLocked && (
                  <DeleteArrayPropertyButton
                    wipConfig={wipConfig}
                    basePropertyKey={basePropertyKey}
                    idx={idx}
                  />
                )}
            </>
          )}
        </BaseOrganizationEntity>
      </Flex>

      <Box margin="20px">
        <Flex alignItems="flex-end">
          <Text fontSize="16px" mb="10px" fontWeight="bold">
            Properties
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKeyWithIdx) && (
            <>
              <AddButton setIsOpen={setIsAddPropertyOpen} />
              <AddPropertyModal
                isOpen={isAddPropertyOpen}
                setIsOpen={setIsAddPropertyOpen}
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKeyWithIdx}
                propertyMap={getIn(
                  wipConfig.creditAgreementFullArtifactPropertyMap,
                  basePropertyKey,
                )}
              />
            </>
          )}
        </Flex>
        <Box mb="20px">
          <PropertyTable
            propertyPairs={propertyPairs}
            basePropertyKey={basePropertyKeyWithIdx}
            wipConfig={wipConfig}
          />
        </Box>
        <Flex alignItems="flex-end">
          <Text fontSize="16px" mb="10px" fontWeight="bold">
            Contacts
          </Text>
          {wipConfig?.isMutateAllowed(`${basePropertyKeyWithIdx}.users`) && (
            <AddIconButton
              ariaLabel="add"
              label="Add"
              onClick={() => {
                const userEntityShell = createUserEntityShell();
                const users = getIn(
                  wipConfig.formProps.values,
                  `${basePropertyKeyWithIdx}.users`,
                );
                const updatedUsersSection = [...users, userEntityShell];
                wipConfig.formProps.setFieldValue(
                  `${basePropertyKeyWithIdx}.users`,
                  updatedUsersSection,
                );
              }}
            />
          )}
        </Flex>
        {party.users.length > 0 ? (
          <Box mb="20px">
            <UserEntityAccordionTable
              users={party.users}
              wipConfig={wipConfig}
              basePropertyKey={`${basePropertyKeyWithIdx}.users`}
              organizationId={party.entity.id}
            />
          </Box>
        ) : (
          <Text>There are no existing contacts for this entity.</Text>
        )}
        {'bankAccounts' in party && (
          <Box mb="20px">
            {(wipConfig?.isMutateAllowed(
              `${basePropertyKeyWithIdx}.bankAccounts`,
            ) ||
              party.bankAccounts.length > 0) && (
              <>
                <Flex alignItems="flex-end">
                  <Text fontSize="16px" mb="10px" fontWeight="bold">
                    Bank Accounts
                  </Text>
                  {wipConfig?.isMutateAllowed(
                    `${basePropertyKeyWithIdx}.bankAccounts`,
                  ) && (
                    <>
                      <AddIconButton
                        ariaLabel="add"
                        label="Add"
                        onClick={() => {
                          const bankAccountEntityShell = createBankAccountEntityShell();
                          const bankAccounts = getIn(
                            wipConfig.formProps.values,
                            `${basePropertyKeyWithIdx}.bankAccounts`,
                          );
                          const updatedBankAccountsSection = [
                            ...bankAccounts,
                            bankAccountEntityShell,
                          ];
                          wipConfig.formProps.setFieldValue(
                            `${basePropertyKeyWithIdx}.bankAccounts`,
                            updatedBankAccountsSection,
                          );
                        }}
                      />
                    </>
                  )}
                </Flex>
                {party.bankAccounts.length > 0 ? (
                  <BankAccountEntityAccordionTable
                    bankAccounts={party.bankAccounts}
                    wipConfig={wipConfig}
                    basePropertyKey={`${basePropertyKeyWithIdx}.bankAccounts`}
                    organizationId={party.entity.id}
                  />
                ) : (
                  <Text>
                    There are no existing bank accounts for this entity.
                  </Text>
                )}
              </>
            )}
          </Box>
        )}
        {'_config_' in party && (
          <Box mb="20px">
            <Flex alignItems="flex-end" mb="2">
              <Text fontSize="16px" mb="2" fontWeight="bold">
                Config
              </Text>
              {wipConfig?.isMutateAllowed(
                `${basePropertyKeyWithIdx}._config_`,
              ) &&
                party._config_ && (
                  <>
                    <EditButton setIsOpen={setIsEditConfigOpen} />
                    <EditPartyEntityConfigModal
                      setIsOpen={setIsEditConfigOpen}
                      isOpen={isEditConfigOpen}
                      basePropertyKey={`${basePropertyKeyWithIdx}._config_`}
                      partyKey={partyKey}
                      _config_={party._config_}
                      wipConfig={wipConfig}
                    />
                  </>
                )}
            </Flex>
            {party._config_ && (
              <PartyEntityConfig
                partyKey={partyKey}
                _config_={party._config_}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
